/**
 * Render quality profile (VQ-001 / AD-194) — the PURE, node-testable core of the
 * tier-based quality degradation path. Like plan.ts/budget.ts it imports NO
 * `three` and does no I/O, so it runs under `node --test` with no renderer.
 *
 * The profile is resolved ONCE at Game construction from the existing WR.3.4
 * `RenderTier` (desktop/mobile) plus the URL search params, and drives the
 * `PostProcessingStack` (postfx.ts) and the scene builder's shadow config:
 *
 * - desktop → full pipeline: bloom + SSAO + AgX tone mapping + full shadow maps.
 * - mobile, or an explicit `?quality=low` → NO SSAO, NO bloom, shadow maps
 *   halved, AgX tone mapping RETAINED (cheap, large perceptual gain — AD-194 §3).
 * - `?bloom=threshold,strength,radius` tweaks the bloom calibration in dev
 *   (partial values allowed; invalid slots keep their defaults; clamped).
 *
 * Everything here is presentation-only and off the per-tick path: no sim, no
 * wire, no collision, no economy surface. Import boundary (AD-122): frontend-
 * local; no shared/server imports at all.
 */

import type { RenderTier } from './budget.js';

/** Bloom pass calibration (UnrealBloomPass ctor order: strength, radius, threshold). */
export interface BloomParams {
  threshold: number;
  strength: number;
  radius: number;
}

/** Shadow-map configuration (renderer + sun light). */
export interface ShadowParams {
  enabled: boolean;
  /** Square shadow-map resolution, texels per side. */
  mapSize: number;
}

/** The resolved, readonly per-session quality profile (AD-194 §3). */
export interface QualityProfile {
  readonly tier: RenderTier;
  /** True when degraded (mobile tier, or an explicit `?quality=low`). */
  readonly low: boolean;
  /** Bloom pass calibration, or `null` ⇒ the pass is disabled. */
  readonly bloom: BloomParams | null;
  /** Screen-space ambient-occlusion pass enabled. */
  readonly ssao: boolean;
  /** AgX tone mapping — retained on EVERY profile (AD-194 §3). */
  readonly agx: true;
  readonly shadows: ShadowParams;
}

/** Art-calibrated bloom defaults (conservative: bright emissives glow, UI does not blow out). */
export const BLOOM_DEFAULTS: Readonly<BloomParams> = {
  threshold: 0.85,
  strength: 0.35,
  radius: 0.4,
};

/** Full-quality shadow-map resolution; the degraded profile halves it (AD-194 §3). */
export const SHADOW_MAP_FULL = 2048;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Parse the `?bloom=threshold,strength,radius` debug override (AD-194 §2a).
 * Partial values are allowed (`?bloom=0.9` sets the threshold only); a slot that
 * is missing or not a finite number keeps its default; all slots are clamped to
 * sane ranges. An absent/empty param returns the defaults.
 */
export function parseBloomParams(search: string): BloomParams {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(search).get('bloom');
  } catch {
    raw = null;
  }
  const out: BloomParams = { ...BLOOM_DEFAULTS };
  if (raw === null || raw === '') return out;
  const slots = raw.split(',').map((s) => Number(s.trim()));
  const [threshold, strength, radius] = slots;
  if (typeof threshold === 'number' && Number.isFinite(threshold)) {
    out.threshold = clamp(threshold, 0, 1);
  }
  if (typeof strength === 'number' && Number.isFinite(strength)) {
    out.strength = clamp(strength, 0, 3);
  }
  if (typeof radius === 'number' && Number.isFinite(radius)) {
    out.radius = clamp(radius, 0, 1);
  }
  return out;
}

/** Whether the search params force the degraded profile (`?quality=low`). */
export function qualityForcedLow(search: string): boolean {
  try {
    return new URLSearchParams(search).get('quality') === 'low';
  } catch {
    return false;
  }
}

/**
 * Resolve the per-session quality profile from the render tier + URL search
 * params (AD-194 §3). Pure: same inputs → same profile.
 */
export function resolveQualityProfile(tier: RenderTier, search = ''): QualityProfile {
  const low = tier === 'mobile' || qualityForcedLow(search);
  if (low) {
    return {
      tier,
      low: true,
      bloom: null,
      ssao: false,
      agx: true,
      shadows: { enabled: true, mapSize: SHADOW_MAP_FULL / 2 },
    };
  }
  return {
    tier,
    low: false,
    bloom: parseBloomParams(search),
    ssao: true,
    agx: true,
    shadows: { enabled: true, mapSize: SHADOW_MAP_FULL },
  };
}

/** The composer pass sequence the profile implies (consumed by PostProcessingStack). */
export type PostPass = 'render' | 'ssao' | 'bloom' | 'output';

/**
 * The EffectComposer pass plan for a profile: `[]` means "no composer — render
 * directly" (the degraded path; AgX still applies via renderer.toneMapping).
 * A non-empty plan always opens with 'render' and closes with 'output' (the
 * OutputPass applies tone mapping + sRGB when writing to screen).
 */
export function passPlan(profile: QualityProfile): PostPass[] {
  const middle: PostPass[] = [];
  if (profile.ssao) middle.push('ssao');
  if (profile.bloom !== null) middle.push('bloom');
  if (middle.length === 0) return [];
  return ['render', ...middle, 'output'];
}

// ---------------------------------------------------------------------------
// Readonly singleton (AD-194 §3): resolved once by Game at startup; future
// per-setting overrides layer on top of this accessor without refactoring the
// renderers that read it.
// ---------------------------------------------------------------------------

let active: QualityProfile | null = null;

/** Install the session's resolved profile (called once by Game's constructor). */
export function setActiveQualityProfile(profile: QualityProfile): void {
  active = profile;
}

/** The active profile; defaults to a full desktop profile until Game resolves one. */
export function activeQualityProfile(): QualityProfile {
  return active ?? resolveQualityProfile('desktop');
}
